<template>
  <div class="child-course-form__container">
    <div class="temp-container">
      <el-form
        ref="childCourseForm"
        :model="form"
        :rules="validateRules"
        label-position="top"
        label-width="100px"
      >
        <el-row :gutter="15">
          <el-col :span="12">
            <el-form-item label="일자">
              <el-date-picker
                class="responsive-visible desktop"
                v-model="presentData.day"
                type="date"
                :disabled="isPast"
                :clearable="false"
                @input="onChangeDate"
              />
              <input-date-mobile
                class="responsive-visible mobile"
                :disabled="isPast"
                :minimum-date="curDatetime"
                v-model="presentData.day"
                minimum-date-warning-message="오늘 이전 날짜는 선택할 수 없습니다."
                @change="onChangeDate"
                @input="onChangeDate"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="시간">
              <el-time-picker
                class="responsive-visible desktop"
                v-model="presentData.time"
                value-format="HH:mm"
                format="HH:mm"
                :picker-options="{
                  selectableRange: [
                    '00:00:00 - 01:00:00',
                    '05:00:00 - 23:59:00'
                  ]
                }"
                :disabled="isPast"
                :clearable="false"
                @input="onChangeDate"
              />
              <input-time-mobile
                class="responsive-visible mobile"
                :disabled="isPast"
                :minimum-date="curDatetime"
                :date="presentData.day"
                minimum-date-warning-message="현재 이전 시간은 선택할 수 없습니다."
                v-model="presentData.time"
                @change="onChangeDate"
                @input="onChangeDate"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="form-divide-line" />
        <el-row :gutter="15">
          <el-col :span="24">
            <el-form-item label="수업">
              <el-select
                ref="courseSelectRef"
                class="form-item__course-selector"
                v-model="form.parent_course"
                placeholder="수업을 선택해주세요"
                :disabled="isPast"
                @input="onSelectParentCourse"
              >
                <el-option-group
                  ref="courseOptionRef"
                  v-for="group in parentCourseCategoryOptions"
                  :key="group.id"
                  :label="group.label"
                >
                  <el-option
                    v-for="item in group.options"
                    :key="item.id"
                    :label="item.label"
                    :value="item.value"
                    :disabled="item.is_removed"
                  >
                    <section :class="item.is_removed? 'option-wrapper__removed' : 'option-wrapper__used'">
                      <span class="option-label">
                        {{ item.label }}
                      </span>
                      <span class="option-removed">
                        {{ item.is_removed ? '삭제됨' : '' }}
                      </span>
                    </section>
                  </el-option>
                </el-option-group>

                <router-link
                  class="btn-add-new-class"
                  to="/setting/course"
                >
                  + 신규수업 개설하기
                </router-link>

                <template slot="empty">
                  <div class="el-select-dropdown__empty">
                    수업 데이터 없음
                  </div>

                  <div>
                    <router-link
                      class="btn-add-new-class"
                      to="/setting/course"
                    >
                      + 신규수업 개설하기
                    </router-link>
                  </div>
                </template>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row  
          :gutter="15"
          v-show="!form.is_offline"
        >
          <el-col :span="24">
            <el-form-item prop="online_course_url">
              <div
                class="online-course-url__label" 
                slot="label"
              >
                온라인 수업 링크
                <popover>
                  온라인으로 진행하실 수업의 링크를 입력해 주세요.<br>
                  본 수업에 예약한 회원은 앱에서 ‘참가’ 버튼을<br>
                  누르면 해당 링크로 바로 이동할 수 있습니다.
                </popover>
              </div>
              <el-input
                type="url"
                v-model="form.online_course_url"
                clearable
                placeholder="https://www.example.com"
                :disabled="isPast"
                @input="increaseFormChangeCount"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="15">
          <el-col :span="12">
            <el-form-item label="강사">
              <el-select
                ref="instructorSelectRef"
                v-model="form.instructors"
                placeholder="강사를 모두 선택해주세요"
                multiple
                collapse-tags
                :disabled="isPast"
                @input="detectChangeSkipFormValue"
              >
                <el-option
                  ref="instructorOptionRef"
                  v-for="item in instructorOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                  :disabled="decideItemDisabled(item)"
                >
                  <section :class="item.is_removed? 'option-wrapper__removed' : 'option-wrapper__used'">
                    <span class="option-label">
                      {{ item.label }}
                    </span>
                    <span class="option-removed">
                      {{ item.is_removed ? '삭제됨' : '' }}
                    </span>
                  </section>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="수업 시간">
              <div class="el-input-group__unit">
                <el-input-number
                  key="duration"
                  v-model="form.duration"
                  controls-position="right"
                  :min="1"
                  :disabled="isPast"
                  @input="detectChangeSkipFormValue"
                />
                <div class="input-unit">
                  분
                </div>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="15">
          <el-col :span="12">
            <el-form-item :label="'차감 ' + membershipSystem.long">
              <div class="el-input-group__unit">
                <el-input-comma-number
                  key="price"
                  v-model="form.price"
                  controls-position="right"
                  :min="0"
                  @change="detectChangeSkipFormValue"
                  @input="detectChangeSkipFormValue"
                  :disabled="isPast || (isUpdateForm ? hasBooking? true : false : false)"
                />
                <div class="input-unit">
                  {{ membershipSystem.short }}
                </div>
              </div>
            </el-form-item>
          </el-col>
          <el-col 
            :span="12" 
            class="col-group__quota"
          >
            <el-form-item
              :label="presentQuotaLabel"
              prop="quota"
            >
              <div class="el-input-group__unit">
                <el-input-number
                  key="quota"
                  v-model="form.quota"
                  controls-position="right"
                  :min="0"
                  :disabled="isPast"
                  label="-"
                  ref="quotaInput"
                  :class="is_quota_valid ? 'quota-input__valid': 'quota-input__invalid'"
                  @input="detectChangeSkipFormValue"
                />
                <div class="input-unit">
                  명
                </div>
                <p 
                  class="quota-valid-msg" 
                  v-show="!is_quota_valid"
                >
                  현재 예약 인원보다 높게 설정해야 합니다.
                </p>
                <el-checkbox
                  class="childcourse-form__waiting-checkbox"
                  v-model="form.is_support_wait_booking"
                  @change="detectChangeSkipFormValue"
                  :disabled="isPast || !isBookingSystem || hasWaitMembers"
                >
                  <p>
                    예약 대기 기능을 지원합니다.
                  </p>
                  <popover placement="bottom">
                    예약 대기 기능 지원 시 정원이 마감되더라도<br>
                    회원은 예약 대기를 신청할 수 있습니다.<br>
                    결원 발생 시마다 대기자 선착순으로 자동 충원되며<br>
                    확정자에게 예약 완료 푸시 알림이 발송됩니다.
                  </popover>
                  <div
                    class="waiting-item__mask"
                    v-if="isWhitePlan || isGreyPlan || isLightGreyPlan"
                    @click.prevent="detectFunction(RESTRICT_FUNCTION_ID.WAITLIST)"
                  />
                </el-checkbox>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <div class="form-divide-line" />

        <el-row :gutter="15">
          <el-col :span="24">
            <el-form-item label="반복 개설">
              <el-select
                v-model="recurrenceForm.frequency"
                :disabled="isPast"
                @input="onChangeFrequency"
              >
                <el-option
                  v-for="option in FREQUENCY_OPTIONS"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <template v-if="recurrenceForm.frequency != 0">
          <el-row :gutter="15">
            <el-col :span="24">
              <el-form-item label="요일">
                <el-checkbox-group
                  v-model="recurrenceForm.weekdays"
                  :disabled="isPast"
                  class="recurrence-form__checkbox"
                  @input="increaseRecurrenceFormChangeCount"
                >
                  <el-checkbox-button
                    v-for="(day, index) in WEEKDAY_OPTIONS"
                    :key="index"
                    :label="day.value.weekday"
                  >
                    {{ day.label }}
                  </el-checkbox-button>
                </el-checkbox-group>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="15">
            <el-col :span="12">
              <el-form-item label="종료">
                <el-date-picker
                  class="responsive-visible desktop"
                  v-model="recurrenceForm.until"
                  :disabled="isPast"
                  :picker-options="datePickerOption"
                  @input="increaseRecurrenceFormChangeCount"
                />

                <input-date-mobile
                  class="responsive-visible mobile"
                  :disabled="isPast"
                  :minimum-date="curDatetime"
                  :maximum-date="nextYearDatetime"
                  v-model="recurrenceForm.until"
                  minimum-date-warning-message="오늘 이전 날짜는 선택할 수 없습니다."
                  maximum-date-warning-message="오늘로부터 1년 후까지만 선택할 수 있습니다."
                  @input="increaseRecurrenceFormChangeCount"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <div style="color: #4a4a4a; height: 50px; line-height: 50px; margin-top: 30px;">
                까지 개설
              </div>
            </el-col>
          </el-row>
        </template>
      </el-form>
    </div>

    <dialog-action-buttons
      ref="dialogActionButtons"
      class="course-dialog__footer"
      :disable-cancel="isPast"
      :cancel-label="data.id === null ? '취소' : '수업 삭제'"
      @cancel="handleClickCancel"
      @confirm="handleClickSave"
    />
  </div>
</template>

<script>
import dateFns from 'date-fns';
import resizePageMixin from '@/mixins/resizePageMixin';
import { RRule } from 'rrule';
import {
  FREQUENCY_OPTIONS,
  WEEKDAY_OPTIONS,
  RESTRICT_FUNCTION_ID,
  RESTRICT_FUNCTION_NAME,
  RESTRICT_FUNCTION_MESSAGE
} from '@/constants';

import ChildCourseDeleteMethods from './ChildCourseDeleteMethods';
import ChildCourseUpdateMethods from './ChildCourseUpdateMethods';
import ChildCourseCreateMethods from './ChildCourseCreateMethods';
import Popover from '@/components/popover';
import InputDateMobile from '@/components/InputDateMobile';
import InputTimeMobile from '@/components/InputTimeMobile';
import DialogActionButtons from '@/components/dialogActionButtons';
import ElInputCommaNumber from '@/components/element-ui/ElInputCommaNumber';
import { fiflboxApi } from '@/utils/api';
import { computedGetters } from '@/utils/vuex-helper';
import { validateUrl } from '@/utils/form-validate-rules';
import {
  makeCategoriesForSelectBox,
  makeInstructorsForSelectBox,
  sortRemoveForSelectBox
} from '@/utils/array-helper';

export default {
  name: 'ClassEditForm',
  components: {
    InputTimeMobile,
    InputDateMobile,
    DialogActionButtons,
    Popover,
    ElInputCommaNumber
  },
  mixins: [
    resizePageMixin,
    ChildCourseCreateMethods,
    ChildCourseUpdateMethods,
    ChildCourseDeleteMethods
  ],
  props: {
    data: {
      required: true
    },
    isPast: {
      type: Boolean,
      required: true
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    hasBooking: {
      type: Boolean,
      default: false
    }
  },
  created() {
    if (this.form.id) {
      this.isUpdateForm = true;
    }
    this.initializedBasicVal();
  },
  data() {
    return {
      RESTRICT_FUNCTION_ID,
      RESTRICT_FUNCTION_NAME,
      RESTRICT_FUNCTION_MESSAGE,
      presentData: {
        day: null,
        time: null
      },
      FREQUENCY_OPTIONS,
      WEEKDAY_OPTIONS,
      form: {
        id: null,

        dt: null, // 실제 시간
        start: null, // 수업의 첫 시간
        parent_course: null,
        online_course_url: null,

        instructors: [],
        duration: null, // 분
        price: null, // 차감횟수

        quota: null, // 예약정원

        is_offline: true, //수업 유형
        to_single: false,
        is_init: true,
        is_support_wait_booking: false
      },
      validateRules: {
        online_course_url: [{ validator: validateUrl, trigger: 'blur' }]
      },
      recurrenceForm: {
        frequency: 0,
        weekdays: [],
        until: null
      },
      initDataForSelectBox: {
        parentCourse: null,
        instructors: []
      },
      recurrenceFormChangeCount: 0,
      formChangeCount: 0,
      isChangeSkipFormValue: false,
      allBookings: [],
      isUpdateForm: false,
      is_quota_valid: true,
      hasWaitMembers: false
    };
  },
  computed: {
    ...computedGetters([
      'parentCourseCategories',
      'instructors',
      'parentCourseById',
      'membershipSystem',
      'isWhitePlan',
      'isGreyPlan',
      'isLightGreyPlan'
    ]),
    numOfCurWeekday() {
      if (!this.presentData.day) {
        return null;
      }

      return this.getDayOfWeek(this.presentData.day);
    },
    datePickerOption() {
      let dt = this.presentData.day;

      return {
        disabledDate(time) {
          return (
            dateFns.isBefore(time.getTime(), dt) ||
            dateFns.isAfter(time.getTime(), dateFns.addYears(dt, 1))
          );
        }
      };
    },
    parentCourseCategoryOptions() {
      return makeCategoriesForSelectBox(
        this.parentCourseCategories,
        'parent_courses',
        this.initDataForSelectBox.parentCourse
      );
    },
    instructorOptions() {
      let instructorOptions = makeInstructorsForSelectBox(
        this.instructors,
        this.initDataForSelectBox.instructors
      );

      return sortRemoveForSelectBox(instructorOptions);
    },
    isBookingSystem() {
      return this.$store.getters.getIsBookingSystem;
    },
    presentQuotaLabel() {
      return this.isBookingSystem ? '예약 정원' : '수업 정원';
    },
    curDatetime() {
      return new Date();
    },
    nextYearDatetime() {
      return dateFns.addYears(this.curDatetime, 1);
    }
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(value) {
        this.initFormFromData(value);
      }
    },
    isOpen: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.initializedBasicVal();
        }
      }
    }
  },
  methods: {
    decideItemDisabled(item) {
      if (this.form.instructors.includes(item.value)) {
        return false;
      }

      return item.is_removed;
    },
    async handleClickSave() {
      let isSuccess;

      if (this.form.id) {
        isSuccess = await this.updateChildCourse();
      } else {
        isSuccess = await this.createChildCourse();
      }

      this.$refs['dialogActionButtons'].stopLoading();

      if (!isSuccess) return;

      this.$emit('closeDialog');
      this.$emit('update');
      this.$message({
        type: 'success',
        message: '수업이 저장됐습니다.'
      });
    },
    handleClickCancel() {
      if (this.data.id) {
        return this.handleClickDelete();
      }

      this.$confirm(
        '수업 개설 취소 시 모든 내용은 복구되지 않습니다.',
        '수업 개설 취소',
        {
          confirmButtonText: '확인',
          cancelButtonText: '취소',
          showClose: false,
          distinguishCancelAndClose: true,
          customClass: 'fifl-message-box',
          confirmButtonClass: 'gray',
          cancelButtonClass: 'white',
          closeOnPressEscape: false
        }
      )
        .then(() => {
          this.$emit('closeDialog');
        })
        .catch(err => {});
    },

    async handleClickDelete() {
      let deleteResult = await this.deleteChildCourse();

      if (!deleteResult) {
        return;
      }

      this.$emit('closeDialog');
      this.$emit('update');
    },

    initFormFromData(data) {
      let value = _.cloneDeep(data);
      this.form = value;
      if (value.parent_course) {
        this.form.parent_course = value.parent_course.id;
        this.form.is_offline = value.is_offline;
      } else {
        this.form.parent_course = null;
      }
      this.initDataForSelectBox.parentCourse = this.form.parent_course;
      this.initDataForSelectBox.instructors = this.form.instructors;

      this.presentData.day = value.dt;
      this.presentData.time = dateFns.format(value.dt, 'HH:mm');

      if (this.form.start === null) {
        this.form.start = value.dt;
      }

      this.recurrenceForm = this.convertRecurrenceDataToForm(
        this.form.recurrence
      );

      this.recurrenceFormChangeCount = 0;
      this.formChangeCount = 0;
      this.initializedBasicVal();
      if (this.form.id && this.form.dt) {
        this.checkWaitMembers(this.form.id, this.form.dt);
      }
    },
    initializedBasicVal() {
      this.form.is_init = true;
      this.is_quota_valid = true;
    },
    detectChangeSkipFormValue() {
      this.is_quota_valid = true;
      this.isChangeSkipFormValue = true;
    },
    increaseFormChangeCount() {
      this.formChangeCount++;
    },
    increaseRecurrenceFormChangeCount() {
      this.recurrenceFormChangeCount++;
    },
    vaildateRecurrenceForm(recurrenceForm) {
      return {
        freq: RRule.WEEKLY,
        weekdays: recurrenceForm.weekdays,
        interval: recurrenceForm.frequency,
        until: new Date(recurrenceForm.until)
      };
    },
    convertRecurrenceDataToForm(recurrenceData) {
      let endOfCurMonth = dateFns.endOfMonth(
        dateFns.addDays(this.presentData.day, 1)
      );
      let endOfYear = dateFns.endOfYear(this.presentData.day);

      if (!recurrenceData) {
        return {
          frequency: 0,
          weekdays: [this.numOfCurWeekday],
          until: endOfCurMonth
        };
      }

      return {
        frequency: recurrenceData.interval,
        weekdays: recurrenceData.weekdays,
        until: recurrenceData.until || endOfYear
      };
    },
    async onChangeFrequency() {
      this.increaseRecurrenceFormChangeCount();
    },
    onChangeDate() {
      let date = this.joinDatetime(this.presentData.day, this.presentData.time);
      this.form.dt = date;
      this.increaseFormChangeCount();
      this.$emit('changeDate', date);
    },
    async onSelectParentCourse(value) {
      this.increaseFormChangeCount();
      let parentCourse = this.parentCourseById(value);
      this.form.instructors = parentCourse.instructors;
      this.initDataForSelectBox.instructors = parentCourse.instructors;
      this.form.duration = parentCourse.duration;
      this.form.price = parentCourse.price;
      if (!this.hasWaitMembers) {
        this.form.quota = parentCourse.quota;
      }
      this.form.is_offline = parentCourse.is_offline;
    },
    joinDatetime(date, time) {
      let day = dateFns.startOfDay(date);
      const [hours, minutes] = time.split(':');

      day = dateFns.setHours(day, hours);
      day = dateFns.setMinutes(day, minutes);
      day = dateFns.setSeconds(day, 0);

      return day;
    },
    getDayOfWeek(dt) {
      return (dateFns.getDay(dt) + 6) % 7;
    },
    onResize() {
      const courseOptionWidth = this.$refs['courseSelectRef'].$el.offsetWidth;
      const instructorOptionWidth = this.$refs['instructorSelectRef'].$el
        .offsetWidth;
      if (this.$refs['courseOptionRef'].length) {
        for (let option of this.$refs['courseOptionRef']) {
          option.$el.style.width = courseOptionWidth + 'px';
        }
      }
      if (this.$refs['instructorOptionRef'].length) {
        for (let option of this.$refs['instructorOptionRef']) {
          option.$el.style.width = instructorOptionWidth + 'px';
        }
      }
    },
    async checkWaitMembers(childCourseId, dt) {
      const params = {
        child_course: childCourseId,
        status: ['WAITING'],
        dt: dt
      };
      const { data: bookings } = await fiflboxApi.v1.getBookings(params);
      this.hasWaitMembers = bookings.length > 0;
    }, // 	async loadAllBooking(childCourseId, dt) {
    //   let { data: bookings } = await fiflboxApi.v1.getBookings({
    //     child_course: childCourseId,
    //     status: ["BOOKED", "PASS"],
    //     dt_after: dt,
    //   })

    // 		this.allBookings = Object.values(_.groupBy(bookings, "dt"))
    // 			.map(bookingArr => bookingArr[0])
    // 			.sort((bookingA, bookingB) =>
    // 				dateFns.compareAsc(bookingA.dt, bookingB.dt)
    // 			);
    // 	},

    async fetchBookings(childCourseId, dt, isBulkAction) {
      const params = {
        child_course: childCourseId,
        status: ['BOOKED', 'PASS', 'WAITING'],
        dt_after: dt
      };

      if (!isBulkAction) {
        params.dt_before = dateFns.endOfDay(dt);
      }

      let { data: bookings } = await fiflboxApi.v1.getBookings(params);

      return Object.values(_.groupBy(bookings, 'dt'))
        .map(bookingArr => bookingArr[0])
        .sort((bookingA, bookingB) =>
          dateFns.compareAsc(bookingA.dt, bookingB.dt)
        );
    },
    detectFunction(funcId) {
      const payload = {
        id: funcId,
        name: RESTRICT_FUNCTION_NAME[funcId],
        message: RESTRICT_FUNCTION_MESSAGE[funcId]
      };
      this.$store.commit('setRestrictFuncInfo', payload);
      this.$store.commit('setIsRestrictFunc', true);
    }
  }
};
</script>

<style lang="scss">
.child-course-form__container {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .temp-container {
    flex: auto;
    overflow: auto;
  }

  .el-form.el-form--label-top {
    min-height: 100%;
    overflow: auto;
  }

  .course-dialog__footer {
    flex: initial;
    padding: 0;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    width: 100%;
  }
  .online-course-url__label {
    display: flex;
    span {
      margin: auto auto auto 3px;
      width: 17px;
      height: 17px;
    }
  }
  .form-item__course-selector {
    input {
      height: 50px;
    }
  }
}
.confirm-booking {
  border-radius: 8px;
  background-color: #f2f2f2;
  height: 177px;
  overflow: auto;
  padding: 10px;
  box-sizing: border-box;

  li {
    font-size: 14px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 20px;
    height: 20px;
    letter-spacing: -0.8px;
    color: #9b9b9b;

    span:first-child {
      display: inline-block;
      width: 143px;
      line-height: 27px;
      height: 27px;
      vertical-align: top;
      margin-right: 7px;
    }

    span:last-child {
      display: inline-block;
      width: calc(100% - 153px);
      height: 27px;
      line-height: 27px;
      text-overflow: ellipsis;
      white-space: nowrap;
      word-wrap: normal;
      overflow: hidden;
      vertical-align: top;
    }
  }
}

.btn-add-new-class {
  box-sizing: border-box;
  height: 30px;
  line-height: 30px;
  width: 100%;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: -0.6px;
  color: #224ba2;
  background-color: white;
  border: white;
  padding: 0 20px;

  &:hover {
    color: #224ba2;
    text-decoration: underline;
  }
}
.col-group__quota {
  .quota-input__invalid {
    input {
      border: 1px solid #dc1200;
    }
  }
  .quota-valid-msg {
    margin: 4px 0 0 4px;
    font-size: 11px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: -0.51px;
    color: #dc1200;
  }
}
.childcourse-form__waiting-checkbox {
  width: 100%;
  display: flex;
  padding: 9px 0 0 0;
  .el-checkbox__input {
    margin: auto 0;
  }

  .waiting-item__mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 999;
  }

  .el-checkbox__label {
    display: flex;
    font-size: 13px;
    font-weight: normal;
    padding-left: 5px;
    p {
      word-break: keep-all;
      white-space: normal;
      line-height: 19px;
      margin: 0;
    }
  }
  .el-popover__reference-wrapper {
    display: flex;
    height: 100%;
    & > img {
      width: 17px;
      height: 17px;
      margin: auto 0;
    }
  }
}
</style>
